import { Box, Grid, Typography, useTheme } from "@mui/material";
import { useState } from "react";
import { toast } from "sonner";

import { ProjectTypeTile } from "Dashboard/Components/ProjectTypeTile";
import type { CountryCodes, LanguageCodes } from "Infrastructure/Api/Api";
import { ModuleStatus } from "Infrastructure/Api/Api";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { useModulesQuery } from "Modules/Api/Queries/useModulesQuery";
import { MODULE_TYPE_CONFIG } from "Modules/Utils/moduleTypeConfig";
import { useCreateProjectMutation } from "Projects/Api/Mutations/useCreateProjectMutation";
import { CreateProjectModal } from "Projects/Components/CreateProjectModal";
import type { CreateProjectFormData } from "Projects/Forms/createProjectFormSchema";
import { dateToApiString } from "Shared/Utils/Format/date";

export const ProjectTypeTiles = (): React.ReactNode => {
  const theme = useTheme();
  const { t } = useResource();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedModuleID, setSelectedModuleID] = useState<string | undefined>(
    undefined,
  );

  const { data: modulesData } = useModulesQuery({
    statusFilter: [ModuleStatus.Active],
    limit: 100,
  });

  const createProjectMutation = useCreateProjectMutation();

  const handleTileClick = (moduleID: string) => {
    setSelectedModuleID(moduleID);
    setIsModalOpen(true);
  };

  const handleClose = () => {
    setIsModalOpen(false);
    setSelectedModuleID(undefined);
  };

  const handleCreateProject = async (projectData: CreateProjectFormData) => {
    await createProjectMutation.mutateAsync({
      name: projectData.name,
      clientID: projectData.clientID,
      moduleID: projectData.moduleID,
      industry: projectData.industry,
      language: projectData.language as LanguageCodes,
      licensedCountries: projectData.licensedCountries as CountryCodes[],
      jobsLimit: projectData.jobsLimit,
      startDate: dateToApiString(projectData.startDate),
      endDate: dateToApiString(projectData.endDate),
      exclusiveContracts: projectData.hasExclusiveContracts
        ? projectData.exclusiveContracts.map((ec) => ({
            actorID: ec.actorID,
            price: ec.price,
            jobLimit: ec.jobLimit,
          }))
        : [],
    });
    toast.success(t(Resources.Projects.CreateProjectModal.SuccessMessage));
    handleClose();
  };

  return (
    <Box>
      <Typography
        sx={{
          fontSize: "20px",
          fontWeight: 600,
          lineHeight: 1,
          marginBottom: theme.spacing(Spacing.L),
          color: theme.palette.text.primary,
        }}
      >
        {t(Resources.Dashboard.CreateNewProject)}
      </Typography>
      <Grid container spacing={Spacing.M}>
        {modulesData?.items.map((module) => {
          const config = MODULE_TYPE_CONFIG[module.type];
          return (
            <Grid key={module.moduleID} size={{ xs: 12, md: 4 }}>
              <ProjectTypeTile
                icon={config.icon}
                iconColor={config.color}
                label={module.name}
                onClick={() => handleTileClick(module.moduleID)}
              />
            </Grid>
          );
        })}
      </Grid>
      <CreateProjectModal
        isOpen={isModalOpen}
        onClose={handleClose}
        onConfirm={handleCreateProject}
        serverError={createProjectMutation.error}
        preselectedModuleID={selectedModuleID}
      />
    </Box>
  );
};
